import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { FileText, Plus, MoreHorizontal, Star, Copy, Trash2, Pencil } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { toast } from "sonner"
import {
  useQuoteTemplates,
  useCreateQuoteTemplate,
  useDeleteQuoteTemplate,
  useSetDefaultQuoteTemplate,
  useDuplicateQuoteTemplate,
} from "@/hooks/use-quote-templates"

export function QuoteTemplatesPage() {
  const navigate = useNavigate()
  const { data, isLoading } = useQuoteTemplates()
  const createMutation = useCreateQuoteTemplate()
  const deleteMutation = useDeleteQuoteTemplate()
  const setDefaultMutation = useSetDefaultQuoteTemplate()
  const duplicateMutation = useDuplicateQuoteTemplate()

  const [createDialog, setCreateDialog] = useState(false)
  const [newName, setNewName] = useState("")

  const templates = data?.data ?? []

  const handleCreate = async () => {
    if (!newName.trim()) return
    const res = await createMutation.mutateAsync({ name: newName.trim() })
    toast.success("Szablon utworzony")
    setCreateDialog(false)
    setNewName("")
    navigate(`/settings/quote-templates/${res.data.id}`)
  }

  const handleDuplicate = async (id: string) => {
    const res = await duplicateMutation.mutateAsync(id)
    toast.success("Szablon zduplikowany")
    navigate(`/settings/quote-templates/${res.data.id}`)
  }

  const handleSetDefault = async (id: string) => {
    await setDefaultMutation.mutateAsync(id)
    toast.success("Ustawiono jako domyślny")
  }

  const handleDelete = async (id: string) => {
    if (!confirm("Czy na pewno chcesz usunąć ten szablon?")) return
    await deleteMutation.mutateAsync(id)
    toast.success("Szablon usunięty")
  }

  return (
    <div className="mx-auto max-w-[800px] space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText size={20} className="text-primary" />
          <h1 className="page-title">
            Szablony ofert
          </h1>
        </div>
        <Button size="sm" onClick={() => setCreateDialog(true)}>
          <Plus size={14} className="mr-1" /> Nowy szablon
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">
        Zarządzaj szablonami ofert. Definiuj układ, style i treści, które będą używane przy generowaniu PDF.
      </p>

      {isLoading && (
        <div className="flex justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      )}

      {!isLoading && (
        <div className="space-y-3">
          {templates.length === 0 ? (
            <div className="rounded-lg border py-12 text-center">
              <FileText size={48} className="mx-auto text-muted-foreground opacity-30" />
              <p className="mt-4 text-sm font-medium text-foreground">Brak szablonów</p>
              <p className="mt-1 text-xs text-muted-foreground">Utwórz pierwszy szablon oferty</p>
              <Button size="sm" className="mt-4" onClick={() => setCreateDialog(true)}>
                <Plus size={14} className="mr-1" /> Utwórz szablon
              </Button>
            </div>
          ) : (
            templates.map((t) => (
              <div
                key={t.id}
                className="flex items-center justify-between rounded-lg border p-4 transition-colors hover:bg-muted cursor-pointer"
                onClick={() => navigate(`/settings/quote-templates/${t.id}`)}
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                    <FileText size={18} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground">{t.name}</span>
                      {t.is_default && (
                        <Badge variant="secondary" className="text-xs gap-1">
                          <Star size={10} /> Domyślny
                        </Badge>
                      )}
                    </div>
                    {t.description && (
                      <p className="text-xs mt-0.5 text-muted-foreground">{t.description}</p>
                    )}
                  </div>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal size={14} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/settings/quote-templates/${t.id}`)}>
                        <Pencil size={14} className="mr-2" /> Edytuj
                      </DropdownMenuItem>
                      {!t.is_default && (
                        <DropdownMenuItem onClick={() => handleSetDefault(t.id)}>
                          <Star size={14} className="mr-2" /> Ustaw jako domyślny
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => handleDuplicate(t.id)}>
                        <Copy size={14} className="mr-2" /> Duplikuj
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDelete(t.id)} className="text-red-600">
                        <Trash2 size={14} className="mr-2" /> Usuń
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nowy szablon oferty</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Nazwa szablonu</label>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm text-foreground outline-none"
                placeholder="np. Szablon premium"
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Anuluj</Button>
            <Button onClick={handleCreate} disabled={!newName.trim()}>Utwórz</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
